import {
  AXES_MSG,
  axesVendorLabel,
  axesWaitingVendorLabel,
  ESTIMATE_PC_MSG,
  LayoutAxis,
  NoteKind,
  pickNamesOf,
  WORK_TYPE_LABEL,
  WorkType,
  type AxisNode,
  type EstimatePlace,
  type ItemList,
  type LineInput,
  type PartnerList,
  type PlaceList,
  type Trade,
  type TradeList,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { api, messageOf } from "../api.js";
import { currentLabels, t } from "../labels.js";

/* ============================================================================
   見積の区分（#129、docs/47、docs/10 §9.1）：場所・工種の列、行の帯の属性、まとめて付ける帯、並べ方のプルダウン。
   並べ方の計算は packages/contract の layoutLines 1か所（画面と PDF が同じ）。属性は紙に出ない（社内用の列）
   ============================================================================ */

type Row = LineInput;

/** 木の並び（1段目 → その下の2段目）。2段目は「1F ＞ LDK」 */
export function treeOptions<T extends { id: string; parentId: string | null; name: string; sortNo: number; archived?: boolean }>(nodes: T[]): Array<{ node: T; label: string; depth: 0 | 1 }> {
  const live = nodes.filter((x) => !x.archived);
  const byOrder = (a: T, b: T) => a.sortNo - b.sortNo || a.name.localeCompare(b.name, "ja");
  const tops = live.filter((x) => !x.parentId || !live.some((p) => p.id === x.parentId)).sort(byOrder);
  return tops.flatMap((t) => [
    { node: t, label: t.name, depth: 0 as const },
    ...live.filter((c) => c.parentId === t.id).sort(byOrder).map((c) => ({ node: c, label: `${t.name} ＞ ${c.name}`, depth: 1 as const })),
  ]);
}

/** id → 名前（短い＝その段の名前、長い＝「1F ＞ LDK」） */
export function namesOf(nodes: AxisNode[]): (id: string | null | undefined) => { short: string; full: string } | null {
  const by = new Map(nodes.map((x) => [x.id, x]));
  return (id) => {
    const n = id ? by.get(id) : undefined;
    if (!n) return null;
    const p = n.parentId ? by.get(n.parentId) : undefined;
    return { short: n.name, full: p ? `${p.name} ＞ ${n.name}` : n.name };
  };
}

const toNode = (x: { id: string; parentId: string | null; name: string; sortNo: number }): AxisNode => ({ id: x.id, parentId: x.parentId, name: x.name, sortNo: x.sortNo });

/**
 * 工種・場所・外注先の一覧（見積の画面で選ぶため）。見積の場所は見積ごと（/estimates/:id/places）、会社の雛形は /places。
 * 場所を作る・雛形から写すと、その場所の id を返す（行に付けるのは画面の保存）
 */
export function useAxesLists(estimateId: string, enabled: { estimate: boolean }, notify: (msg: string) => void) {
  const qc = useQueryClient();
  const trades = useQuery({ queryKey: ["trades"], queryFn: () => api<TradeList>("GET", "/trades"), staleTime: 30_000 });
  const places = useQuery({ queryKey: ["estimate-places", estimateId], queryFn: () => api<PlaceList>("GET", `/estimates/${estimateId}/places`), enabled: enabled.estimate });
  const templates = useQuery({ queryKey: ["places"], queryFn: () => api<PlaceList>("GET", "/places"), enabled: enabled.estimate, staleTime: 30_000 });
  const vendors = useQuery({ queryKey: ["partners", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor"), enabled: enabled.estimate, staleTime: 30_000 });
  // 品目（#118）の既定の単位：単位のプルダウンの先頭に出す（#131 の2回目）
  const items = useQuery({ queryKey: ["items"], queryFn: () => api<ItemList>("GET", "/items"), enabled: enabled.estimate, staleTime: 60_000 });
  const afterPlace = (r: { place: EstimatePlace } & PlaceList): string => {
    qc.setQueryData(["estimate-places", estimateId], { items: r.items });
    return r.place.id;
  };
  /** 「2F ＞ 子供部屋」「2F>子供部屋」は2段で作る（親が無ければ親も） */
  const createPlace = async (text: string): Promise<string> => {
    const [a, b] = text.split(/\s*[＞>]\s*/).map((x) => x.trim()).filter(Boolean);
    if (!a) throw new Error("場所の名前を入れてください");
    const top = afterPlace(await api<{ place: EstimatePlace } & PlaceList>("POST", `/estimates/${estimateId}/places`, { name: a }));
    if (!b) return top;
    return afterPlace(await api<{ place: EstimatePlace } & PlaceList>("POST", `/estimates/${estimateId}/places`, { name: b, parentId: top }));
  };
  const copyPlace = async (fromPlaceId: string): Promise<string> => afterPlace(await api<{ place: EstimatePlace } & PlaceList>("POST", `/estimates/${estimateId}/places`, { fromPlaceId }));
  // 外注先（#149）：name＝略称（displayNameOf）、full＝正式名、label＝選ぶ欄の名前（同じ略称が2社あるときだけ「略称（正式名）」）
  const vendorItems = vendors.data?.items ?? [];
  const vendorLabels = pickNamesOf(vendorItems.map((v) => ({ short: v.displayName, full: v.name })));
  return {
    trades: trades.data?.items ?? [],
    places: places.data?.items ?? [],
    templates: templates.data?.items ?? [],
    vendors: vendorItems.map((v, i) => ({ id: v.id, name: v.displayName, full: v.name, label: vendorLabels[i] ?? v.displayName })),
    /** 品目 → 既定の単位（無ければ空） */
    itemUnit: (id: string | null | undefined): string | null => (id ? ((items.data?.items ?? []).find((x) => x.id === id)?.unit ?? null) : null),
    loaded: { trades: !!trades.data, places: !!places.data },
    notify,
    createPlace,
    copyPlace,
  };
}
export type AxesLists = ReturnType<typeof useAxesLists>;

/** 並べ方の計算に渡す木（下書きはいまの一覧、出した回は固めた名前） */
export function axisNodes(lists: AxesLists, frozen: { trades: AxisNode[]; places: AxisNode[] } | undefined, useFrozen: boolean): { trades: AxisNode[]; places: AxisNode[] } {
  if (useFrozen && frozen) return frozen;
  return {
    trades: lists.loaded.trades ? lists.trades.map(toNode) : (frozen?.trades ?? []),
    places: lists.loaded.places ? lists.places.map(toNode) : (frozen?.places ?? []),
  };
}

/* ---------------------------------------------------------------- 並べ方のプルダウン（Figma PC1：紙の上の帯） */
export function LayoutSelect({ value, onChange, disabled, axes = LayoutAxis.options }: {
  value: LayoutAxis;
  onChange: (v: LayoutAxis) => void;
  disabled?: boolean;
  /** 出す並べ方（使う機能で絞る＝contract の layoutAxesFor。#142 段3） */
  axes?: readonly LayoutAxis[];
}) {
  return (
    <label className="axlayout">
      <span className="muted">{AXES_MSG.layout}</span>
      <select value={value} disabled={disabled} aria-label={AXES_MSG.layout} data-testid="layout-axis" onChange={(e) => onChange(e.target.value as LayoutAxis)}>
        {axes.map((a) => (
          <option key={a} value={a}>{t(AXES_MSG.axis[a])}</option>
        ))}
      </select>
    </label>
  );
}

/* ---------------------------------------------------------------- 場所・工種の列（社内用。狭いとしるしだけ、乗せると全部の名前） */
export function AxisCell({ r, place, trade, selected, onSelect }: {
  r: Row;
  place: { short: string; full: string } | null;
  trade: { short: string; full: string } | null;
  selected?: boolean | undefined;
  onSelect?: ((on: boolean) => void) | undefined;
}) {
  return (
    <td className="int ax" data-testid="axis-cell">
      <div className="axcell">
        {onSelect ? (
          <input type="checkbox" className="axsel" checked={!!selected} aria-label={`${r.name || "この行"}を選ぶ`} data-testid="axis-select" onChange={(e) => onSelect(e.target.checked)} />
        ) : null}
        <span className="axchips">
          {place ? <span className="axchip pl" title={`${AXES_MSG.place}：${place.full}`} data-testid="axis-place">{place.short}</span> : null}
          {trade ? <span className="axchip tr" title={`${t(AXES_MSG.trade)}：${trade.full}`} data-testid="axis-trade">{trade.short}</span> : null}
        </span>
      </div>
    </td>
  );
}

/* ---------------------------------------------------------------- 場所のプルダウン（見積の場所 → 会社の雛形から写す → その場で作る） */
const KEEP = "__keep";
const NEW = "__new";
/** 空の選択肢（#131 の2回目：「（場所なし）」などの説明をやめて「—」） */
const EMPTY = "—";
export function PlaceSelect({ id, value, lists, onChange, onAskNew, keep, disabled }: {
  id: string;
  value: string | null | typeof KEEP;
  lists: AxesLists;
  onChange: (v: string | null) => void;
  /** 「＋ 新しい場所…」：名前を聞いて作る（画面の名前のダイアログ）。作った場所の id を onChange に渡す */
  onAskNew: (done: (placeId: string) => void) => void;
  /** まとめて付ける帯：「変更しない」を出す */
  keep?: boolean;
  disabled?: boolean;
}) {
  const mine = treeOptions(lists.places);
  const have = new Set(mine.map((x) => x.label));
  const tmpl = treeOptions(lists.templates).filter((x) => !have.has(x.label));
  return (
    <select
      id={id}
      value={value ?? ""}
      disabled={disabled}
      aria-label={AXES_MSG.place}
      data-testid={id}
      onChange={async (e) => {
        const v = e.target.value;
        if (v === KEEP) return;
        if (v === NEW) {
          onAskNew((placeId) => onChange(placeId));
          return;
        }
        if (v.startsWith("tmpl:")) {
          try {
            onChange(await lists.copyPlace(v.slice(5)));
          } catch (err) {
            lists.notify(messageOf(err));
          }
          return;
        }
        onChange(v || null);
      }}
    >
      {keep ? <option value={KEEP}>（変更しない）</option> : null}
      <option value="">{EMPTY}</option>
      {mine.map((x) => (
        <option key={x.node.id} value={x.node.id}>{x.depth ? `　${x.label}` : x.label}</option>
      ))}
      {tmpl.length ? (
        <optgroup label="場所のテンプレートから">
          {tmpl.map((x) => (
            <option key={x.node.id} value={`tmpl:${x.node.id}`}>{x.depth ? `　${x.label}` : x.label}</option>
          ))}
        </optgroup>
      ) : null}
      <option value={NEW}>＋ 新しい場所…</option>
    </select>
  );
}

export function TradeSelect({ id, value, trades, onChange, keep, disabled }: { id: string; value: string | null | typeof KEEP; trades: Trade[]; onChange: (v: string | null) => void; keep?: boolean; disabled?: boolean }) {
  return (
    <select id={id} value={value ?? ""} disabled={disabled} aria-label={t(AXES_MSG.trade)} data-testid={id} onChange={(e) => e.target.value !== KEEP && onChange(e.target.value || null)}>
      {keep ? <option value={KEEP}>（変更しない）</option> : null}
      <option value="">{EMPTY}</option>
      {treeOptions(trades).map((x) => (
        <option key={x.node.id} value={x.node.id}>{x.depth ? `　${x.label}` : x.label}</option>
      ))}
    </select>
  );
}

export function WorkTypeSelect({ id, value, onChange, keep }: { id: string; value: string | null | typeof KEEP; onChange: (v: WorkType | null) => void; keep?: boolean }) {
  return (
    <select id={id} value={value ?? ""} aria-label={AXES_MSG.workType} data-testid={id} onChange={(e) => e.target.value !== KEEP && onChange((e.target.value || null) as WorkType | null)}>
      {keep ? <option value={KEEP}>（変更しない）</option> : null}
      <option value="">{EMPTY}</option>
      {WorkType.options.map((w) => (
        <option key={w} value={w}>{WORK_TYPE_LABEL[w]}</option>
      ))}
    </select>
  );
}

export function VendorSelect({ id, value, vendors, onChange, keep }: { id: string; value: string | null | typeof KEEP; vendors: Array<{ id: string; name: string; label?: string }>; onChange: (v: string | null) => void; keep?: boolean }) {
  return (
    <select id={id} value={value ?? ""} aria-label={axesVendorLabel(currentLabels())} data-testid={id} onChange={(e) => e.target.value !== KEEP && onChange(e.target.value || null)}>
      {keep ? <option value={KEEP}>（変更しない）</option> : null}
      <option value="">{EMPTY}</option>
      {vendors.map((v) => (
        <option key={v.id} value={v.id}>{v.label ?? v.name}</option>
      ))}
    </select>
  );
}

/* ---------------------------------------------------------------- 行の詳細（書いている行の下の1行）の 場所・工種・材工・外注・注記・外注待ち（#131 の2回目：1行に並べる）。
   外注を選ぶと仕入れの「自社」は外れる（外注先の付いた行は自社にしない） */
export function AxisStrip({ r, lists, upd, onAskNew, labor, field = true, places = true, trades = true }: {
  r: Row;
  lists: AxesLists;
  upd: (p: Partial<Row>) => void;
  onAskNew: (done: (placeId: string) => void) => void;
  /** 材工のあとに置くもの（労務費の印） */
  labor?: ReactNode;
  /** モジュール field（現場力だけ）。無い会社は材工区分を出さない（#142 段2。値は残す） */
  field?: boolean;
  /** 使う機能（#142 段3）：場所・{trade}。OFF なら欄を出さない（値は残す） */
  places?: boolean;
  trades?: boolean;
}) {
  const textRow = r.role === "note" || r.role === "excluded";
  return (
    <>
      {places ? (
        <label className="axf" data-testid="axis-strip">
          <span className="muted">{AXES_MSG.place}</span>
          <PlaceSelect id="f_place" value={r.placeId} lists={lists} onChange={(v) => upd({ placeId: v })} onAskNew={onAskNew} />
        </label>
      ) : null}
      {trades ? (
        <label className="axf">
          <span className="muted">{t(AXES_MSG.trade)}</span>
          <TradeSelect id="f_trade" value={r.tradeId} trades={lists.trades} onChange={(v) => upd({ tradeId: v })} />
        </label>
      ) : null}
      {r.role === "detail" && field ? (
        <label className="axf">
          <span className="muted">{AXES_MSG.workType}</span>
          <WorkTypeSelect id="f_worktype" value={r.workType} onChange={(v) => upd({ workType: v })} />
        </label>
      ) : null}
      {labor}
      {r.role === "detail" || textRow ? (
        <label className="axf">
          <span className="muted">{axesVendorLabel(currentLabels())}</span>
          <VendorSelect id="f_vendor" value={r.vendorPartnerId} vendors={lists.vendors} onChange={(v) => upd(v ? { vendorPartnerId: v, costOwn: false } : { vendorPartnerId: v })} />
        </label>
      ) : null}
      {textRow ? (
        <label className="axf">
          <span className="muted">注記</span>
          <select id="f_notekind" value={r.noteKind ?? ""} aria-label="注記の種類" data-testid="f_notekind" onChange={(e) => upd({ noteKind: (e.target.value || null) as NoteKind | null })}>
            <option value="">{EMPTY}</option>
            {NoteKind.options.map((k) => (
              <option key={k} value={k}>{AXES_MSG.noteKind[k]}</option>
            ))}
          </select>
        </label>
      ) : null}
      {r.role === "note" ? (
        <label className="chk axf">
          <input type="checkbox" checked={r.lineState === "provisional"} data-testid="f_waiting" onChange={(e) => upd({ lineState: e.target.checked ? "provisional" : "final" })} />
          {axesWaitingVendorLabel(currentLabels())}
        </label>
      ) : null}
    </>
  );
}

/** 仮の行（スマホで作った行）だけ：仮のメモと「確定にする」（行の詳細の2行目） */
export function ProvisionalStrip({ r, upd, onFinalize }: { r: Row; upd: (p: Partial<Row>) => void; onFinalize: () => void }) {
  if (!(r.role === "detail" && r.lineState === "provisional")) return null;
  return (
    <div className="row dline axprov">
      <span className="chip prov">{AXES_MSG.provisional}</span>
      <input className="ce axnote" id="f_provnote" value={r.provisionalNote ?? ""} placeholder="仮のメモ" aria-label="仮のメモ" onChange={(e) => upd({ provisionalNote: e.target.value || null })} />
      <button type="button" className="small ai" data-testid="finalize-line" onClick={onFinalize}>{AXES_MSG.finalize}</button>
    </div>
  );
}

/* ---------------------------------------------------------------- まとめて付ける帯（行を選んでいるとき） */
export type BulkPatch = Partial<Pick<Row, "placeId" | "tradeId" | "workType" | "vendorPartnerId">>;
export function BulkBar({ count, provisional, lists, onApply, onFinalize, onClear, onAskNew, moveTargets, onMove, onCopy, field = true, places = true, trades = true }: {
  count: number;
  /** 選んだ行のうち仮の行の数 */
  provisional: number;
  lists: AxesLists;
  onApply: (p: BulkPatch) => void;
  onFinalize: () => void;
  onClear: () => void;
  onAskNew: (done: (placeId: string) => void) => void;
  /** 内訳へ移動（#138）：移す先（表紙＋内訳、いまの紙は除く）。選んだ瞬間に移す。内訳の無い見積・出した回では渡さない */
  moveTargets?: Array<{ id: string; label: string }> | undefined;
  onMove?: ((sheetId: string) => void) | undefined;
  /** 選んだ行の複製（#199）。選んだ行の直後に写す。渡さないときは出さない */
  onCopy?: (() => void) | undefined;
  /** モジュール field（現場力だけ）。無い会社は材工区分を出さない（#142 段2） */
  field?: boolean;
  /** 使う機能（#142 段3）：場所・{trade} */
  places?: boolean;
  trades?: boolean;
}) {
  const [p, setP] = useState<{ placeId: string | null | typeof KEEP; tradeId: string | null | typeof KEEP; workType: WorkType | null | typeof KEEP; vendorPartnerId: string | null | typeof KEEP }>({ placeId: KEEP, tradeId: KEEP, workType: KEEP, vendorPartnerId: KEEP });
  const patch: BulkPatch = Object.fromEntries(Object.entries(p).filter(([, v]) => v !== KEEP)) as BulkPatch;
  return (
    <div className="axbulk" data-testid="axis-bulk">
      <b className="num">{count}行を選択中</b>
      {places ? (
        <label className="axf">
          <span className="muted">{AXES_MSG.place}</span>
          <PlaceSelect id="bulk_place" keep value={p.placeId} lists={lists} onChange={(v) => setP({ ...p, placeId: v })} onAskNew={onAskNew} />
        </label>
      ) : null}
      {trades ? (
        <label className="axf">
          <span className="muted">{t(AXES_MSG.trade)}</span>
          <TradeSelect id="bulk_trade" keep value={p.tradeId} trades={lists.trades} onChange={(v) => setP({ ...p, tradeId: v })} />
        </label>
      ) : null}
      {field ? (
        <label className="axf">
          <span className="muted">{AXES_MSG.workType}</span>
          <WorkTypeSelect id="bulk_worktype" keep value={p.workType} onChange={(v) => setP({ ...p, workType: v })} />
        </label>
      ) : null}
      <label className="axf">
        <span className="muted">{axesVendorLabel(currentLabels())}</span>
        <VendorSelect id="bulk_vendor" keep value={p.vendorPartnerId} vendors={lists.vendors} onChange={(v) => setP({ ...p, vendorPartnerId: v })} />
      </label>
      <button type="button" className="btn primary" data-testid="bulk-apply" disabled={!Object.keys(patch).length} onClick={() => onApply(patch)}>
        {AXES_MSG.bulkApply}
      </button>
      {provisional ? (
        <button type="button" className="btn" data-testid="bulk-finalize" onClick={onFinalize}>{AXES_MSG.finalize}（{provisional}行）</button>
      ) : null}
      {moveTargets?.length && onMove ? (
        <label className="axf">
          <span className="muted">{ESTIMATE_PC_MSG.menuMoveSheet}</span>
          <select id="bulk_sheet" value="" aria-label={ESTIMATE_PC_MSG.menuMoveSheet} data-testid="bulk-move-sheet" onChange={(e) => e.target.value && onMove(e.target.value)}>
            <option value="">{EMPTY}</option>
            {moveTargets.map((x) => (
              <option key={x.id} value={x.id}>{x.label}</option>
            ))}
          </select>
        </label>
      ) : null}
      {onCopy ? (
        <button type="button" className="btn" onClick={onCopy} data-testid="bulk-copy">{ESTIMATE_PC_MSG.rowsCopy}</button>
      ) : null}
      <button type="button" className="btn text" onClick={onClear} data-testid="bulk-clear">取消</button>
    </div>
  );
}
